import { useMutation, useQueryClient } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { encodeFunctionData, parseEther } from "viem";
import { waitForTransactionReceipt } from "viem/actions";
import { useAccount, useChainId, useWalletClient } from "wagmi";

import { stakingAbi } from "shared/config/abi/staking-abi";
import { galacticaStakeContractConfig } from "shared/config/const";
import { catchError, notifySuccess } from "shared/ui/toast";

type Params = {
  amount: string;
  validatorAddress: string;
};

export const useDelegateMutation = () => {
  const { address: accountAddress } = useAccount();
  const chainId = useChainId();
  const { data: wc } = useWalletClient({ chainId });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ amount, validatorAddress }: Params) => {
      invariant(wc);
      invariant(accountAddress);

      const contractAddress = galacticaStakeContractConfig[chainId];

      const parsedAmount = parseEther(amount);

      const data = encodeFunctionData({
        abi: stakingAbi,
        functionName: "delegate",
        args: [accountAddress, validatorAddress, parsedAmount],
      });

      console.log({
        data,
        value: parsedAmount,
        to: contractAddress,
        from: accountAddress,
        account: accountAddress,
      });

      const hash = await wc.sendTransaction({
        data,
        value: parsedAmount,
        to: contractAddress,
        from: accountAddress,
        account: accountAddress,
      });

      const receipt = await waitForTransactionReceipt(wc, { hash });

      return receipt;
    },
    onError: catchError,
    onSuccess: async (_, vars) => {
      await queryClient.invalidateQueries({
        queryKey: ["cosmos"],
      });
      notifySuccess({
        message: "You have successfully delegated your GNET to a validator!",
        badge: `Amount GNET: ${vars.amount}`,
      });
    },
  });
};
